import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Lock, RotateCcw, Users } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useUserGroups } from "@/lib/hooks/use-user-groups";
import { useRecordGrants, type GrantLevel, type RecordGrant } from "@/lib/hooks/use-record-grants";

export type ShareableRecordType = "engagement" | "file" | "note";

interface ShareRecordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recordId: string;
  recordName: string;
  /** Copy/analytics only — the backend infers the entity from the record id. */
  recordType: ShareableRecordType;
}

const DEFAULT_LABEL_KEYS: Record<string, string> = {
  private: "sharing.visPrivate",
  manager: "sharing.visManager",
  managers_and_admins: "sharing.visManagersAndAdmins",
  everyone: "sharing.visEveryone",
};

/**
 * Per-record group sharing (replace-all): pick groups and a view/edit level per
 * group; saving writes the whole state as "custom" grants. "Reset to workspace
 * default" hands the record back to the per-entity default. Rendered as a
 * standard Radix Dialog — safe when opened from detail Sheets (AddToListDialog
 * precedent; the no-createPortal rule targets manual portals).
 */
export default function ShareRecordDialog({
  open,
  onOpenChange,
  recordId,
  recordName,
  recordType,
}: ShareRecordDialogProps) {
  const { t } = useTranslation();
  const { allGroups, loading: groupsLoading } = useUserGroups();
  const {
    grants,
    source,
    workspaceDefault,
    loading: grantsLoading,
    saveGrants,
    resetToDefault,
  } = useRecordGrants(recordId, { skip: !open });

  // Draft state, seeded from the loaded grants each time the dialog opens or
  // fresh data arrives while it is open.
  const [draft, setDraft] = useState<Map<string, GrantLevel>>(new Map());
  const [saving, setSaving] = useState(false);

  const loading = groupsLoading || grantsLoading;
  const grantsKey = useMemo(
    () => grants.map((g) => `${g.groupId}:${g.level}`).join(","),
    [grants]
  );

  useEffect(() => {
    if (!open) return;
    setDraft(new Map(grants.map((g) => [g.groupId, g.level])));
    // grantsKey stands in for grants so a refetch with identical content
    // doesn't clobber in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, grantsKey]);

  const toggleGroup = (groupId: string) => {
    setDraft((prev) => {
      const next = new Map(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.set(groupId, "view");
      return next;
    });
  };

  const setLevel = (groupId: string, level: GrantLevel) => {
    setDraft((prev) => new Map(prev).set(groupId, level));
  };

  const finish = async (action: () => Promise<{ success: boolean; error?: string }>) => {
    setSaving(true);
    const result = await action();
    setSaving(false);
    if (result.success) {
      toast.success(t("sharing.accessUpdated"));
      onOpenChange(false);
    } else {
      toast.error(result.error || t("sharing.accessUpdateFailed"));
    }
  };

  const handleSave = () =>
    finish(() => {
      const next: RecordGrant[] = Array.from(draft.entries()).map(([groupId, level]) => ({
        groupId,
        level,
      }));
      return saveGrants(next);
    });

  const handleReset = () => finish(() => resetToDefault());

  const defaultLabel =
    workspaceDefault && DEFAULT_LABEL_KEYS[workspaceDefault]
      ? t(DEFAULT_LABEL_KEYS[workspaceDefault])
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="truncate pr-6">
            {t("sharing.shareRecordTitle", { name: recordName })}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {source === "custom" ? (
              <>
                <Lock className="h-3.5 w-3.5 shrink-0" />
                <span>{t("sharing.customAccess")}</span>
              </>
            ) : (
              <>
                <Users className="h-3.5 w-3.5 shrink-0" />
                <span>
                  {defaultLabel
                    ? t("sharing.workspaceDefaultLabel", { default: defaultLabel })
                    : t("sharing.visibleToEveryone")}
                </span>
              </>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : allGroups.length === 0 ? (
            <p className="py-4 text-sm text-muted-foreground">{t("sharing.noGroupsYet")}</p>
          ) : (
            <div className="rounded-lg border border-border divide-y divide-border max-h-64 overflow-y-auto">
              <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground bg-muted/40">
                {t("sharing.sharedWithGroups")}
              </div>
              {allGroups.map((group) => {
                const level = draft.get(group.id);
                return (
                  <div key={group.id} className="flex items-center gap-2.5 px-3 py-2">
                    <Checkbox
                      id={`share-group-${group.id}`}
                      checked={draft.has(group.id)}
                      onCheckedChange={() => toggleGroup(group.id)}
                    />
                    <label
                      htmlFor={`share-group-${group.id}`}
                      className="flex-1 min-w-0 cursor-pointer"
                    >
                      <span className="flex items-center gap-1.5 text-sm">
                        <span className="truncate">{group.name}</span>
                        {group.systemKey && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">
                            {t("sharing.systemGroup")}
                          </Badge>
                        )}
                      </span>
                    </label>
                    {level && (
                      <select
                        className="h-7 rounded-md border border-input bg-background px-1.5 text-xs"
                        value={level}
                        onChange={(e) => setLevel(group.id, e.target.value as GrantLevel)}
                        aria-label={t("sharing.accessLevel")}
                      >
                        <option value="view">{t("sharing.canView")}</option>
                        <option value="edit">{t("sharing.canEdit")}</option>
                      </select>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <p className="text-xs text-muted-foreground">{t("sharing.ownersAlwaysHaveAccess")}</p>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="gap-1.5"
            onClick={handleReset}
            disabled={saving || loading}
          >
            <RotateCcw className="h-3.5 w-3.5" />
            {t("sharing.resetToDefault")}
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={saving}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button type="button" size="sm" onClick={handleSave} disabled={saving || loading}>
              {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              {t("common.save", "Save")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
